// The REMAINING AXIS rule — one place, because it was four.
//
// A limitation's "remaining" is not a column. It is whichever of two stored
// columns the row's own `based_on` selects:
//
//     based_on ∈ {Date (Months), Date (Days), Date (Years)}  ->  days_remaining
//     otherwise                                              ->  units_remaining
//
// PROVENANCE — this rule is not invented here. It already ships, inline, in
// four places in this module, and this file is a verbatim extraction of it:
//   · aircraftInspectionDisplay.ts:buildAircraftInspectionPdfRows  (stationery)
//   · AircraftInspectionLimitations.tsx:438, :453-472, :488-495     (grid)
// Measured on origin/main @ a40b80a8d. The list above is the extraction's
// evidence, NOT a to-do: the four call sites are deliberately left untouched
// here (they carry their own tests and a sibling lane's edits), and collapsing
// them onto this helper is its own diff.
//
// ── WHAT THIS FILE DOES NOT DO ─────────────────────────────────────────────
// It COMPUTES NOTHING. It selects between two values the server already
// stored. That distinction is load-bearing:
//
//   · AP-63 — the airworthiness values on `aircraft_inspection_log` have NO
//     WRITER. They are correct at insert and decay from then on. Rendering a
//     stored value is honest; deriving a fresh one here would invent an
//     airworthiness number the system cannot stand behind.
//   · AP-64 — that row claimed the legacy calculation engine is not in this
//     repo. IT IS FALSE AND CLOSED. The engine is on disk at
//     `/Users/aaron/Boris/Legacy code/htdocs/`, and the SUPPRESSION half of it
//     is read and honoured below (see THE ENGINE'S OWN GATE). What is still not
//     ported is the ARITHMETIC — see the next bullet.
//   · AP-76/AP-78 — of the rules the arithmetic spec established, 17 are
//     UNDERDETERMINED, and legacy's `green` INVERTS if copied. So this file
//     exposes ONE derived predicate — `isOverdue`, i.e. remaining < 0 — and
//     deliberately no warning/amber/green band. A traffic light needs the
//     `warning` + `warning_unit` comparison that AP-78 says we cannot yet
//     reproduce; `warning_color` in the DB is unwritten (empty on every g2
//     row measured 2026-09-20), so there is nothing to read either.
import type { InspectionLimitation } from "./types";

// ── THE ENGINE'S OWN GATE — QUOTED, NOT PARAPHRASED ────────────────────────
//
// Two surfaces in this module each grew their own `remaining` helper and they
// disagreed on ONE row class: `On Condition`. The aircraft record's Inspections
// tab excluded it from overdue; the inspection record's fleet-wide tab did not.
// Nothing failed, because each helper served one screen — so the SAME airframe
// could render overdue on one and normal on the other.
//
// It is not a judgement call. The engine that WRITES these columns is on disk
// and answers it. `aircraft_check_warning`
// (`Legacy code/htdocs/mod_aircraft/api/aircraft.inc:8`) binds
// `abf.table_name = aircraft_inspection_log` for `limitation-type is Inspection`
// (`:259`), selects the active rows (`:271-273`), loops them (`:282`), and for
// each row decides whether to compute anything AT ALL:
//
//     348:  <if variable="abf_sql.based_on is &modcfg.aircraft.condition_based_on_list;"><set variable="abf.proceed" value="0" /></if>
//
// `proceed = 0` is then read by the guard that wraps the whole calculation:
//
//     367:  <if variable="abf_sql.active is 0"><set variable="abf.proceed" value="0" /></if>
//     368:  <if variable="abf.proceed is 1">
//     ...
//     382:    <aircraft_set_warning
//     ...
//     416:    <append variable="abf.insert_qry" value=", (&abf_sql.limitation_id;, '&abf.warning_type;', '&abf.remaining_days;', '&abf.remaining_units;')" />
//     417:  </if>
//     418:  <else>
//     419:    <append variable="abf.insert_qry" value=", (&abf_sql.limitation_id;, '', 0, 0)" />
//     420:  </else>
//
// and `abf.insert_qry` is what lands on the row (`:432-436`):
//
//     update &abf.table_name; source_table, `&abf.tablename;` temp_table
//     set source_table.warning_color = temp_table.warning_color, source_table.warning_processed = now(),
//     source_table.units_remaining = temp_table.units_remaining, source_table.days_remaining = temp_table.days_remaining
//     where source_table.id = temp_table.id
//
// ANSWER TO THE QUESTION "for which rows does the engine compute a remaining
// value, and what does it do with On Condition":
//
//   · It computes one only for rows that reach `:382` — i.e. `active = 1`, a
//     `based_on` NOT in `condition_based_on_list`, and a usable next value
//     (`next_date` matching `????-??-??` and != `0000-00-00` on the date axis,
//     `:350-353`; `next_units > 0` on the units axis, `:355-356`).
//   · For a condition-based row it computes NOTHING. It takes the `:418` else
//     branch and stores `warning_color = ''`, `days_remaining = 0`,
//     `units_remaining = 0`. Not a colour, not a number, not a negative.
//
// So a condition-based row is NEVER overdue in the engine, and cannot be: the
// engine never writes it a negative. I-A's exclusion is the engine's behaviour
// and I-D's inclusion is not. This file now carries the exclusion, and BOTH
// surfaces read it from here.
//
// ⚠️ THE LIST HAS THREE MEMBERS, NOT TWO. `modcfg.aircraft.condition_based_on_list`
// is built by three appends in `mod_modules/upgrade_notes_revised.inc:76-80`,
// quoted verbatim:
//
//     76:  <config dev="joby" name="modcfg.aircraft.condition_based_on_list" value="On Condition" append=""/>
//     78:  <config dev="joby" name="modcfg.aircraft.condition_based_on_list" value="Not Applicable" append=""/>
//     80:  <config dev="joby" name="modcfg.aircraft.condition_based_on_list" value="Untracked" append=""/>
//
// `Untracked` is the THIRD member and neither helper had it. Our backend has
// this list twice at two members (`app/aircraft/schemas.py:1652`, `:680`) and
// once at three (`fleet_report_compute.py:113-123`, which adds `Untracked` from
// config) — parity doc L-04. Following the engine picks the three-member form.
// Measured 2026-09-21 before widening it, so the widening is provably inert on
// the data we have: `aircraft_inspection_log` holds 455 rows in
// `module_g2_demo` and 15 in `module_aeronet_demo`, and `based_on = 'Untracked'`
// matches ZERO of them (`On Condition` also zero; `Not Applicable` one row in
// g2, with neither remaining column negative). It is a latent divergence being
// closed, not a live defect being fixed.
//
// ── WHAT IS *NOT* TAKEN FROM THE ENGINE ────────────────────────────────────
// The engine has two further suppressions this file cannot evaluate, so this
// predicate stays a SUPERSET of the engine's `red` — it can colour a row the
// engine would blank:
//   · `aircraft.inc:437-441`, a post-pass `update … set warning_color = ''` for
//     rows whose `status` is in `modcfg.aircraft.warning_not_applicable_status`.
//     `status` is a column on the table but not a member of
//     `InspectionLimitationList`, so it is not on the wire.
//   · `no_warning_inspection_type` (`:319-324`, applied at `:394-397`), driven
//     by the inspection type. Not on the wire either.
// And the band logic itself (`aircraft_set_warning`, `:556-727`) is NOT ported:
// `red_warning` is `datediff(next, greatest(date_of_values, curdate())) < 0` on
// the date axis (`:643`) and `next - <counter> < 0` on the units axis (`:682`),
// both computed in MySQL against live aircraft counters we do not hold here.
// What IS implemented is strictly: the STORED remaining on this row's own axis
// is negative.

/**
 * The three `based_on` values that put a limitation on the DATE axis.
 *
 * Quoted verbatim from the shipping `based_on_date` arrays cited above —
 * including the exact spelling and parenthesisation, which are DATA: they are
 * compared against `aircraft_inspection_log.based_on`, a free-ish string
 * column. Re-spelling "Date (Months)" as "Date (months)" silently moves every
 * calendar limitation onto the units axis.
 */
export const DATE_AXIS_BASED_ON = [
  "Date (Months)",
  "Date (Days)",
  "Date (Years)",
] as const;

/**
 * `based_on` values the ENGINE refuses to compute a remaining for.
 *
 * The three appends at `upgrade_notes_revised.inc:76-80`, quoted in the header.
 * This is the ONE copy in this module — `aircraftInspectionDisplay.ts`
 * re-exports this binding rather than keeping a second list, because a second
 * list is how the two surfaces came to disagree in the first place.
 */
export const CONDITION_BASED_ON = [
  "On Condition",
  "Not Applicable",
  "Untracked",
] as const;

/** Whitespace is not data here; 0 rows in `module_g2_demo` carry any (measured 2026-09-21). */
function norm(basedOn: string | null | undefined): string {
  return (basedOn || "").trim();
}

/** True when this row's remaining is measured in DAYS rather than units. */
export function isDateAxis(basedOn: string | null | undefined): boolean {
  return DATE_AXIS_BASED_ON.includes(
    norm(basedOn) as typeof DATE_AXIS_BASED_ON[number]
  );
}

/**
 * `based_on` is condition-based — the engine takes `aircraft.inc:418` and
 * stores `('', 0, 0)`. No colour, no number, and never a negative.
 */
export function isConditionBased(basedOn: string | null | undefined): boolean {
  return CONDITION_BASED_ON.includes(
    norm(basedOn) as typeof CONDITION_BASED_ON[number]
  );
}

/**
 * This row is one the ENGINE would have computed a remaining for.
 *
 * It is the whole `proceed` gate we can evaluate from the wire, and it is ONE
 * predicate on purpose: two clauses, one of which implies the other, is a
 * swallowed guard — sabotaging it changes nothing and the test that "covers" it
 * proves nothing. (Measured: an earlier draft of this file had
 * `!isAxisKnown(...) || isConditionBased(...)`, and deleting the second half
 * failed 0 of 61 tests.) The falsifiable surface is `CONDITION_BASED_ON`
 * itself, and `isOverdue` calls only this.
 *
 *   · condition-based → the engine never computes one (`aircraft.inc:348` →
 *     `:418-419`, which stores `('', 0, 0)`).
 *   · blank / NULL axis → nothing to name. 3 such rows exist in
 *     `module_g2_demo` and all carry remaining 0.
 *
 * ⚠️ WIDER THAN THE ENGINE, DELIBERATELY. The engine resolves the axis through
 * `aircraft_get_based_on_field` plus a per-aircraft `aircraft_custom_based_on`
 * lookup (`aircraft.inc:596-617`) and computes nothing when BOTH come back
 * empty (`:618`); we cannot run either query from the client. So any non-empty,
 * non-condition `based_on` counts as computable here. That leaves this a
 * SUPERSET of the engine's red — the recoverable direction, since the number is
 * rendered in the cell beside the colour.
 */
export function isAxisKnown(basedOn: string | null | undefined): boolean {
  const b = norm(basedOn);
  return isDateAxis(b) || (b !== "" && !isConditionBased(b));
}

/**
 * The row's remaining, on whichever axis `based_on` selects.
 *
 * Returns `null` — NOT 0 — when the selected column is itself null. The two
 * mean different things: 0 means "due right now", null means "this row has no
 * remaining recorded on its own axis". Inspection 1 on g2 is exactly this case
 * for the date axis: all five rows carry `days_remaining = NULL` while
 * `units_remaining` is populated, so a `|| 0` here would print a confident
 * "0 days" on five rows that have no day count at all.
 *
 * ⚠️ NEVER CLAMPED. A negative remaining is real and means OVERDUE — g2's
 * inspection 1 carries units_remaining = -11.00 on log row 178 (C-GDNB),
 * measured 2026-09-20. Math.max(0, …) anywhere on this path would erase the
 * one row a maintenance controller most needs to see.
 */
type RemainingRow = Pick<InspectionLimitation, "based_on" | "days_remaining" | "units_remaining"> &
  Partial<Pick<InspectionLimitation, "live_days_remaining" | "live_units_remaining">>;

/**
 * The raw remaining on the row's own axis: the LIVE figure when the server computed
 * one (Aaron 2026-10-04: Remaining "computed live ... the way Components serve it" --
 * the stored figures have no writer after insert, AP-63, so C-FFAF's GARMIN database
 * printed "3 days" three days after it fell due), else the stored one. Selection,
 * not arithmetic: the computing is app/aircraft/aircraft_warning.py:due_reading.
 */
export function axisRemainingRaw(row: RemainingRow): string | number | null | undefined {
  if (isDateAxis(row.based_on)) {
    return row.live_days_remaining ?? row.days_remaining;
  }
  return row.live_units_remaining ?? row.units_remaining;
}

export function resolveRemaining(row: RemainingRow): number | null {
  const raw = axisRemainingRaw(row);
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

/** The unit the resolved remaining is expressed in, for a column header cell. */
export function remainingUnit(basedOn: string | null | undefined): string {
  return isDateAxis(basedOn) ? "days" : "units";
}

/**
 * OVERDUE = the engine would have computed a remaining for this row, AND the
 * stored remaining on the row's own axis is strictly below zero.
 *
 * THE SINGLE PREDICATE FOR BOTH SURFACES. The aircraft record's Inspections tab
 * reaches it through `getInspectionRemainingState`; the inspection record's
 * fleet-wide tab calls it directly.
 *
 * Three ways to be not-overdue, each for its own reason:
 *   · condition-based axis — the engine stores `('', 0, 0)` and never a
 *     negative (`aircraft.inc:348` → `:418-419`). THIS is the clause the two
 *     helpers disagreed on.
 *   · unnameable axis — nothing to reason about.
 *   · `null` remaining — absent is unknown, not past due. Returning true would
 *     paint every unrecorded row red.
 *
 * 🔴 `extension` IS NOT A PARAMETER AND MUST NOT BECOME ONE. The engine assigns
 * `green` only to a row that is NOT ALREADY RED (`aircraft.inc:399-401`), so
 * green is an "extension granted" marker and red survives an extension. A guard
 * that suppressed overdue on an extended row was written here once and it
 * painted a past-due airframe as normal.
 */
export function isOverdue(row: RemainingRow): boolean {
  // ONE clause. `isAxisKnown` is the engine's `proceed` gate and already
  // refuses a condition-based axis; adding `|| isConditionBased(...)` here
  // reads like a second guard and is a swallowed one.
  if (!isAxisKnown(row.based_on)) return false;
  const r = resolveRemaining(row);
  // `Number.isFinite` and not just `!== null`: it is the guard the aircraft
  // tab's inline version carried, so keeping it keeps that surface's verdict
  // byte-identical. `resolveRemaining` already nulls a NaN (the legacy `'-'`);
  // this additionally refuses a -Infinity, which `< 0` would otherwise accept.
  return r !== null && Number.isFinite(r) && r < 0;
}
